import { SEMANTIC_COLORS } from '@/constants/colors.constant'
import { SectorCompositionItem } from '@/services/whatIfAnalysis.service'
import { Box, Typography } from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useMemo } from 'react'

interface SectorCompProps {
  data?: SectorCompositionItem[]
  loading?: boolean
  error?: string | null
}

const SectorComp: React.FC<SectorCompProps> = ({ 
  data = [], 
  loading = false, 
  error = null,
}) => {
  
  
  const chartOptions = useMemo(() => {
    let chartData: SectorCompositionItem[] = []

    if (!data || data.length === 0) {
      chartData = []
    } else {
      const sectorOrder = [
        'treasury',
        'agency',
        'corporates',
        'mortgage', 
        'other',
      ]

      const getOrderIndex = (sectorLabel: string): number => {
        const label = sectorLabel.toLowerCase()

        for (let i = 0; i < sectorOrder.length; i += 1) {
          if (label.includes(sectorOrder[i])) {
            return i
          }
        }

        // Unmatched sectors come after the known ones, grouped by original position
        return sectorOrder.length
      }

      chartData = [...data]
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const orderA = getOrderIndex(a.item.sector)
          const orderB = getOrderIndex(b.item.sector)

          if (orderA !== orderB) {
            return orderA - orderB
          }

          return a.index - b.index
        })
        .map((wrapped) => wrapped.item)
    }

    // Extract sector names for categories
    const categories = chartData.map(item => item.sector)

    return {
      chart: {
        type: 'column',
        height: 500
      },
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: categories,
        title: {
          text: 'Sectors'
        }
      },
      yAxis: {
        title: {
          text: 'Composition (%)'
        },
        min: 0,
        max: 100,
        labels: {
          formatter: function(): string {
            return `${(this as any).value.toFixed(2)}%`
          }
        }
      },
      tooltip: {
        formatter: function(): string {
          return `<b>${(this as any).x}</b><br/>${(this as any).series.name}: ${(this as any).y.toFixed(2)}`
        }
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: false,
          }
        }
      },
      series: [
        {
          name: 'Portfolio Before',
          color: SEMANTIC_COLORS.PORTFOLIO_BEFORE,
          data: chartData.map(item => Number(item.composition_0))
        },
        {
          name: 'Portfolio After',
          color: SEMANTIC_COLORS.PORTFOLIO_AFTER,
          data: chartData.map(item => Number(item.composition_1))
        },
        {
          name: 'Benchmark',
          color: SEMANTIC_COLORS.BENCHMARK,
          data: chartData.map(item => Number(item.composition_b))
        }
      ]
    }
  }, [data])

  if (loading) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="h6" color="text.secondary">
          Loading Sector Composition Data...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
      />
    </Box>
  )
}

export default SectorComp
